import React, { useState, useEffect, useRef, useCallback } from "react";
import { RouteComponentProps, Link } from "react-router-dom";
import { api } from "../api";
import { ConfirmModal } from "../DesignSystem/ConfirmModal";
import {
  getAudioElement,
  playAudio,
  pauseAudio,
  clearQueue,
  playSongNow,
  userStore,
  registerAudioElement,
  useQueue,
  useUserStatus,
} from "../state";
import { CompoJSON } from "../models/compos";
import {
  SyncListenJSON,
  SyncListenPublicEntryJSON,
} from "../../../src/apidefs";
import {
  MusicalNoteIcon,
  ChatBubbleLeftRightIcon,
  ListBulletIcon,
  CheckIcon,
  ClockIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { PlayIcon } from "@heroicons/react/24/solid";
import { EntryMainContent } from "../Entry/EntryMainContent";
import { SmallButton } from "../DesignSystem/SmallButton";
import { Tooltip } from "../DesignSystem/Tooltip";
import { SyncListenHeader, SyncConnectionStatus } from "./SyncListenHeader";
import { SyncListenChat } from "./SyncListenChat";
import { SyncListenListeners } from "./SyncListenListeners";
import { TestIds } from "../testIds";

type SyncListenState =
  | { type: "loading" }
  | { type: "error"; message: string }
  | { type: "no-sync-listen"; compo: CompoJSON }
  | {
      type: "loaded";
      compo: CompoJSON;
      syncListen: SyncListenJSON;
      entries: SyncListenPublicEntryJSON[];
    };

export function SyncListenPage(
  props: RouteComponentProps<{ id: string }>
): JSX.Element {
  const compoId = Number(props.match.params.id);
  const [state, setState] = useState<SyncListenState>({ type: "loading" });
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);
  const [isSyncLoading, setIsSyncLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [lastSseEventTime, setLastSseEventTime] = useState<number | null>(null);
  const [connectionStatus, setConnectionStatus] =
    useState<SyncConnectionStatus>("stopped");
  const [syncSecondsIntoEntry, setSyncSecondsIntoEntry] = useState(0);
  const [syncCurrentEntryIndex, setSyncCurrentEntryIndex] = useState(0);
  const [sidebarTab, setSidebarTab] = useState<"chat" | "tracklist" | "listeners">("chat");
  const [listenerCount, setListenerCount] = useState(0);
  const lastConnectionStatusRef =
    useRef<SyncConnectionStatus>("stopped");

  // Get current song URL from queue (reactive) - must be before early returns
  const { songs: queueSongs, currentIndex: queueCurrentIndex } = useQueue();

  // Get user status for admin check
  const { user: userState } = useUserStatus();
  const isAdmin = userState.type === "logged-in" && userState.user.admin;

  // Audio element for sync listen (since main Toolbar is hidden)
  // Use callback ref so we register when element is actually attached to DOM
  const audioRefCallback = useCallback((el: HTMLAudioElement | null) => {
    registerAudioElement(el);
  }, []);

  // Cleanup audio element registration on unmount
  useEffect(() => {
    return () => registerAudioElement(null);
  }, []);

  // Compute connection status based on sync state and last SSE event time
  useEffect(() => {
    if (state.type !== "loaded" || state.syncListen.state === "paused") {
      setConnectionStatus("stopped");
      return;
    }

    // Check connection status every second
    const checkStatus = () => {
      if (lastSseEventTime === null) {
        // Sync is running but we haven't received any events yet
        setConnectionStatus("stale");
      } else {
        const timeSinceLastEvent = Date.now() - lastSseEventTime;
        if (timeSinceLastEvent > 10000) {
          setConnectionStatus("stale");
        } else {
          setConnectionStatus("live");
        }
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 1000);
    return () => clearInterval(interval);
  }, [state, lastSseEventTime]);

  useEffect(() => {
    if (state.type !== "loaded") {
      return;
    }

    if (state.syncListen.state === "paused") {
      pauseAudio();
      setLastSseEventTime(null);
      setSyncCurrentEntryIndex(state.syncListen.currentEntryIndex);
      setSyncSecondsIntoEntry(state.syncListen.currentEntrySeconds);

      const targetEntry =
        state.entries[state.syncListen.currentEntryIndex] ??
        state.entries[0];
      if (!targetEntry) {
        return;
      }

      setSelectedEntryId(targetEntry.entryId);

      const currentSong = queueSongs[queueCurrentIndex];
      if (!currentSong || currentSong.entry_id !== targetEntry.entryId) {
        clearQueue();
        playSongNow({
          name: targetEntry.title,
          uploaded_url: targetEntry.uploadedUrl,
          entry_url: `/entry/${targetEntry.entryId}`,
          entry_id: targetEntry.entryId,
        });
      }
    }
  }, [state, queueSongs, queueCurrentIndex]);

  useEffect(() => {
    const previousStatus = lastConnectionStatusRef.current;
    if (previousStatus === "live" && connectionStatus === "stale") {
      pauseAudio();
    }
    lastConnectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  useEffect(() => {
    const fetchData = async () => {
      const result = await api.get<"/api/compo/:id/sync">(
        `/api/compo/${compoId}/sync`
      );

      if (api.isToast(result)) {
        setState({ type: "error", message: result.msg });
        return;
      }

      if (!result.syncListen) {
        setState({ type: "no-sync-listen", compo: result.compo });
        return;
      }

      setState({
        type: "loaded",
        compo: result.compo,
        syncListen: result.syncListen,
        entries: result.entries,
      });

      // Initialize current entry index from sync state
      setSyncCurrentEntryIndex(result.syncListen.currentEntryIndex);
      setSyncSecondsIntoEntry(result.syncListen.currentEntrySeconds);

      // Select the current entry if available, otherwise fall back to first entry
      if (result.entries.length > 0) {
        const currentEntry =
          result.entries[result.syncListen.currentEntryIndex] ??
          result.entries[0];
        setSelectedEntryId(currentEntry.entryId);
      }
    };

    fetchData();
  }, [compoId]);

  // SSE sync listener
  const entriesRef = useRef<SyncListenPublicEntryJSON[]>([]);
  const queueRef = useRef(userStore.getState().queue);
  const currentQueueIndexRef = useRef(userStore.getState().currentQueueIndex);

  // Keep refs up to date
  useEffect(() => {
    return userStore.subscribe((state) => {
      queueRef.current = state.queue;
      currentQueueIndexRef.current = state.currentQueueIndex;
    });
  }, []);

  useEffect(() => {
    if (state.type !== "loaded") return;
    entriesRef.current = state.entries;
  }, [state]);

  // Fetch listener count periodically
  useEffect(() => {
    if (state.type !== "loaded") return;

    const fetchListenerCount = async () => {
      try {
        const response = await fetch(`/api/compo/${compoId}/sync/listeners`);
        if (response.ok) {
          const data = await response.json();
          // Dedupe by username for the count
          const users: { username: string | null; visitorId: string }[] = data.users || [];
          const uniqueUsers = users.reduce<typeof users>((acc, user) => {
            if (user.username) {
              if (!acc.some((u) => u.username === user.username)) {
                acc.push(user);
              }
            } else {
              acc.push(user);
            }
            return acc;
          }, []);
          setListenerCount(uniqueUsers.length);
        }
      } catch {
        // Ignore errors
      }
    };

    fetchListenerCount();
    const interval = setInterval(fetchListenerCount, 5000);
    return () => clearInterval(interval);
  }, [state.type, compoId]);

  useEffect(() => {
    if (state.type !== "loaded") return;

    // Generate a stable visitorId for this session
    const visitorId = Math.random().toString(36).substring(2);
    let eventSource: EventSource | null = null;

    const setupEventListeners = (es: EventSource) => {
      es.addEventListener("sync-state", (event) => {
        console.log("Received sync-state:", event.data);
        setLastSseEventTime(Date.now());

        const data = JSON.parse(event.data) as {
          currentEntryIndex: number;
          secondsIntoEntry: number;
          state: "paused" | "playing" | "gap_between_entries";
          gapSeconds: number;
          timestamp: number;
        };

        setSyncSecondsIntoEntry(data.secondsIntoEntry);
        setSyncCurrentEntryIndex(data.currentEntryIndex);

        const entries = entriesRef.current;
        console.log(
          "Entries:",
          entries.length,
          "Target index:",
          data.currentEntryIndex,
          "state:",
          data.state
        );
        if (entries.length === 0) return;

        const targetEntry = entries[data.currentEntryIndex];
        if (!targetEntry) {
          console.log("No target entry found");
          return;
        }

        const audioEl = getAudioElement();
        const currentSong = queueRef.current[currentQueueIndexRef.current];
        console.log(
          "Current song:",
          currentSong?.entry_id,
          "Target:",
          targetEntry.entryId,
          "Audio:",
          !!audioEl
        );

        // Check if we need to switch songs
        const needsSongSwitch =
          !currentSong || currentSong.entry_id !== targetEntry.entryId;

        console.log("Needs song switch:", needsSongSwitch);

        if (needsSongSwitch) {
          // Clear queue and play the correct song
          clearQueue();
          playSongNow({
            name: targetEntry.title,
            uploaded_url: targetEntry.uploadedUrl,
            entry_url: `/entry/${targetEntry.entryId}`,
            entry_id: targetEntry.entryId,
          });
          setSelectedEntryId(targetEntry.entryId);

          // After switching, seek to correct position
          setTimeout(() => {
            const newAudioEl = getAudioElement();
            if (newAudioEl && data.secondsIntoEntry > 0) {
              newAudioEl.currentTime = data.secondsIntoEntry;
            }
            // Only play if not paused
            if (data.state === "playing") {
              playAudio();
            }
          }, 100);
          return;
        }

        // Check if we need to seek (timestamp differs by >20 seconds)
        if (audioEl) {
          const currentTime = audioEl.currentTime;
          const diff = Math.abs(currentTime - data.secondsIntoEntry);

          if (diff > 20) {
            audioEl.currentTime = data.secondsIntoEntry;
          }

          // Handle play/pause based on sync state
          if (data.state !== "playing" && !audioEl.paused) {
            pauseAudio();
          } else if (data.state === "playing" && audioEl.paused) {
            playAudio();
          }
        }
      });

      es.addEventListener("sync-stopped", () => {
        pauseAudio();
      });
    };

    // Connect to heartbeat endpoint - this registers us and returns SSE stream
    // Reconnect every 5 seconds to maintain heartbeat
    const connect = () => {
      if (eventSource) {
        eventSource.close();
      }

      console.log("Connecting to sync listen...");
      let heartbeatUrl = `/api/compo/${compoId}/sync/heartbeat?visitorId=${visitorId}`;
      if (userState.type === "logged-in") {
        heartbeatUrl += `&username=${encodeURIComponent(userState.user.username)}`;
        if (userState.user.avatar_url) {
          heartbeatUrl += `&avatarUrl=${encodeURIComponent(userState.user.avatar_url)}`;
        }
      }
      eventSource = new EventSource(heartbeatUrl);

      eventSource.onopen = () => {
        console.log("EventSource connected");
      };

      eventSource.onerror = () => {
        // Connection errors are expected when syncbot is down - handled gracefully
        // via the connection status indicator going stale
        setLastSseEventTime(null);
      };

      setupEventListeners(eventSource);
    };

    // Initial connection
    connect();

    // Reconnect every 5 seconds to maintain heartbeat
    const heartbeatInterval = setInterval(connect, 5000);

    return () => {
      clearInterval(heartbeatInterval);
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [
    state.type,
    state.type === "loaded" ? state.syncListen.state : null,
    compoId,
    userState,
  ]);

  if (state.type === "loading") {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="text-slate-500 dark:text-slate-400">Loading...</div>
      </div>
    );
  }

  if (state.type === "error") {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="text-red-500 dark:text-red-400">{state.message}</div>
      </div>
    );
  }

  if (state.type === "no-sync-listen") {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-slate-100 dark:bg-slate-900">
        <MusicalNoteIcon className="mb-4 h-16 w-16 text-slate-300 dark:text-slate-600" />
        <div className="text-lg text-slate-500 dark:text-slate-400">
          No sync listen has been set up for{" "}
          <Link
            to={`/compo/${compoId}`}
            className="font-semibold text-blue-600 hover:underline dark:text-blue-400"
          >
            {state.compo.name}
          </Link>{" "}
          yet.
        </div>
      </div>
    );
  }

  const handleStartSync = async () => {
    setIsSyncLoading(true);
    const result = await api.post<"/api/compo/:id/sync/start">(
      `/api/compo/${compoId}/sync/start`,
      {}
    );
    setIsSyncLoading(false);
    if (!api.isToast(result) || result.type === "success") {
      // Refresh the page data to get the new sync state
      const refreshResult = await api.get<"/api/compo/:id/sync">(
        `/api/compo/${compoId}/sync`
      );
      if (!api.isToast(refreshResult) && refreshResult.syncListen) {
        setState({
          type: "loaded",
          compo: refreshResult.compo,
          syncListen: refreshResult.syncListen,
          entries: refreshResult.entries,
        });
      }
    }
  };

  const handlePauseSync = async () => {
    setIsSyncLoading(true);
    const result = await api.post<"/api/compo/:id/sync/stop">(
      `/api/compo/${compoId}/sync/stop`,
      {}
    );
    setIsSyncLoading(false);
    if (!api.isToast(result) || result.type === "success") {
      // Refresh the page data to get the new sync state
      const refreshResult = await api.get<"/api/compo/:id/sync">(
        `/api/compo/${compoId}/sync`
      );
      if (!api.isToast(refreshResult) && refreshResult.syncListen) {
        setState({
          type: "loaded",
          compo: refreshResult.compo,
          syncListen: refreshResult.syncListen,
          entries: refreshResult.entries,
        });
      }
    }
  };

  const handleDeleteSync = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteSync = async () => {
    setShowDeleteModal(false);
    setIsSyncLoading(true);
    const result = await api.post<"/api/compo/:id/sync/delete">(
      `/api/compo/${compoId}/sync/delete`,
      {}
    );
    setIsSyncLoading(false);
    if (!api.isToast(result) || result.type === "success") {
      // Refresh the page data to get the reset sync state
      const refreshResult = await api.get<"/api/compo/:id/sync">(
        `/api/compo/${compoId}/sync`
      );
      if (!api.isToast(refreshResult) && refreshResult.syncListen) {
        setState({
          type: "loaded",
          compo: refreshResult.compo,
          syncListen: refreshResult.syncListen,
          entries: refreshResult.entries,
        });
      }
    }
  };

  const handleNextEntry = async () => {
    setIsSyncLoading(true);
    await api.post<"/api/compo/:id/sync/next">(
      `/api/compo/${compoId}/sync/next`,
      {}
    );
    setIsSyncLoading(false);
  };

  const handlePrevEntry = async () => {
    setIsSyncLoading(true);
    await api.post<"/api/compo/:id/sync/prev">(
      `/api/compo/${compoId}/sync/prev`,
      {}
    );
    setIsSyncLoading(false);
  };

  const handleSetDiscordSync = async () => {
    setIsSyncLoading(true);
    const result = await api.post<"/api/compo/:id/sync/set-discord-sync">(
      `/api/compo/${compoId}/sync/set-discord-sync`,
      {}
    );
    setIsSyncLoading(false);
    if (!api.isToast(result) || result.type === "success") {
      // Refresh the page data to get the updated sync state
      const refreshResult = await api.get<"/api/compo/:id/sync">(
        `/api/compo/${compoId}/sync`
      );
      if (!api.isToast(refreshResult) && refreshResult.syncListen) {
        setState({
          type: "loaded",
          compo: refreshResult.compo,
          syncListen: refreshResult.syncListen,
          entries: refreshResult.entries,
        });
      }
    }
  };

  // isSyncRunning means the sync is playing (not paused)
  const isSyncRunning = state.syncListen.state === "playing";
  // isSyncStarted means the tick loop is active
  const isSyncStarted = state.syncListen.lastTickAt !== null;
  const audioSrc = queueSongs[queueCurrentIndex]?.uploaded_url || "";

  return (
    <div
      data-testid={TestIds.SyncListenPage}
      className="flex h-screen flex-col bg-slate-100 dark:bg-slate-900"
    >
      {/* Hidden audio element for sync listen */}
      <audio
        ref={audioRefCallback}
        src={audioSrc}
        preload="auto"
        className="hidden"
      />

      {/* Header */}
      <SyncListenHeader
        compoName={state.compo.name}
        isSyncRunning={isSyncRunning}
        isSyncStarted={isSyncStarted}
        connectionStatus={connectionStatus}
        syncSecondsIntoEntry={syncSecondsIntoEntry}
        lastSseEventTime={lastSseEventTime}
        onStartSync={handleStartSync}
        onPauseSync={handlePauseSync}
        onDeleteSync={handleDeleteSync}
        onNextEntry={handleNextEntry}
        onPrevEntry={handlePrevEntry}
        onSetDiscordSync={handleSetDiscordSync}
        isSyncLoading={isSyncLoading}
        syncedToDiscordChannel={state.syncListen.syncedToDiscordChannel}
      />

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Entry Content */}
        <div className="flex-1 overflow-y-auto bg-slate-50 px-1 py-8 dark:bg-slate-900 sm:px-10 md:px-20 lg:px-32 xl:px-[200px]">
          {selectedEntryId ? (
            <EntryMainContent key={selectedEntryId} entryId={selectedEntryId} />
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <MusicalNoteIcon className="mx-auto mb-4 h-16 w-16 text-slate-300 dark:text-slate-600" />
                <p className="text-slate-500 dark:text-slate-400">
                  Select a song from the tracklist
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar - Tabbed Chat/Tracklist (fixed width) */}
        <div className="flex w-96 flex-col border-l border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          {/* Tab Header */}
          <div className="flex border-b dark:border-slate-700">
            <div className="flex flex-1 justify-center border-slate-200 p-2 dark:border-slate-700">
              <SmallButton
                onClick={() => setSidebarTab("chat")}
                variant={sidebarTab === "chat" ? "default" : "tertiary"}
                icon={<ChatBubbleLeftRightIcon className="h-4 w-4" />}
              >
                Chat
              </SmallButton>
            </div>
            <div className="flex flex-1 justify-center p-2">
              <SmallButton
                onClick={() => setSidebarTab("tracklist")}
                variant={sidebarTab === "tracklist" ? "default" : "tertiary"}
                icon={<ListBulletIcon className="h-4 w-4" />}
              >
                Tracklist
              </SmallButton>
            </div>
            <div className="flex flex-1 justify-center p-2">
              <SmallButton
                onClick={() => setSidebarTab("listeners")}
                variant={sidebarTab === "listeners" ? "default" : "tertiary"}
                icon={<UsersIcon className="h-4 w-4" />}
              >
                <span className="whitespace-nowrap">
                  Listeners{listenerCount > 0 ? ` (${listenerCount})` : ""}
                </span>
              </SmallButton>
            </div>
          </div>

          {/* Tab Content */}
          {sidebarTab === "chat" ? (
            <SyncListenChat
              compoId={compoId}
              syncedToDiscordChannel={state.syncListen.syncedToDiscordChannel}
              isAdmin={isAdmin}
              onSetDiscordSync={handleSetDiscordSync}
            />
          ) : sidebarTab === "listeners" ? (
            <SyncListenListeners compoId={compoId} />
          ) : (
            <div className="flex-1 overflow-y-auto">
              <div className="py-2">
                {state.entries.map((entry, index) => {
                  // Determine status: played, playing, or upcoming
                  const isPlayed = index < syncCurrentEntryIndex;
                  const isPlaying = index === syncCurrentEntryIndex;
                  const isUpcoming = index > syncCurrentEntryIndex;

                  // Calculate estimated play time for upcoming songs
                  let estimatedTime = "";
                  if (isUpcoming && isSyncRunning) {
                    // Sum durations from current song (remaining) + songs in between
                    let secondsUntilPlay = 0;

                    // Remaining time in current song
                    const currentSongDuration =
                      state.entries[syncCurrentEntryIndex]?.durationSeconds ??
                      0;
                    secondsUntilPlay += Math.max(
                      0,
                      currentSongDuration - syncSecondsIntoEntry
                    );

                    // Add durations of songs in between
                    for (let i = syncCurrentEntryIndex + 1; i < index; i++) {
                      secondsUntilPlay +=
                        state.entries[i]?.durationSeconds ?? 0;
                    }

                    // Calculate estimated time
                    const estimatedDate = new Date(
                      Date.now() + secondsUntilPlay * 1000
                    );
                    const hours = estimatedDate.getHours();
                    const minutes = estimatedDate.getMinutes();
                    const ampm = hours >= 12 ? "PM" : "AM";
                    const displayHours = hours % 12 || 12;
                    const displayMinutes = minutes.toString().padStart(2, "0");
                    estimatedTime = `~${displayHours}:${displayMinutes} ${ampm}`;
                  }

                  const iconElement = isPlaying ? (
                    <PlayIcon className="h-5 w-5 shrink-0 text-yellow-500" />
                  ) : isPlayed ? (
                    <CheckIcon className="h-5 w-5 shrink-0 text-green-500" />
                  ) : (
                    <ClockIcon className="h-5 w-5 shrink-0 text-slate-400" />
                  );

                  return (
                    <button
                      key={entry.entryId}
                      onClick={() => setSelectedEntryId(entry.entryId)}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left transition hover:bg-slate-50 dark:hover:bg-slate-700/50 ${
                        selectedEntryId === entry.entryId
                          ? "bg-slate-100 dark:bg-slate-700"
                          : ""
                      }`}
                    >
                      {estimatedTime ? (
                        <Tooltip
                          element={iconElement}
                          tooltip={estimatedTime}
                        />
                      ) : (
                        iconElement
                      )}
                      <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-200">
                        {entry.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteSync}
        title="Delete sync listen?"
        confirmText="Delete"
        confirmVariant="danger"
      >
        <p>
          This will entirely remove the state of the sync listen. It will not
          remember which songs have been played, and you will need to start from
          scratch.
        </p>
      </ConfirmModal>
    </div>
  );
}
